import { v4 as uuidv4 } from "uuid";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import db from "../../Firestore/FirestoreConfig";
import {
  CLIENT_NUTRITION_DEFAULT_PURPOSE,
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../Firestore/nutrition/nutritionPaths";
import {
  buildClientNutritionPlanSnapshot,
  sortClientNutritionPlans,
} from "../../Firestore/nutrition/nutritionRecords";
import { buildNutritionPlanDuplicateDisplayName } from "./nutritionPlanNameUtils";
import { resolveNutritionPlanActionSnapshot } from "./nutritionPlanActionSnapshot";
import { resolveClientNutritionWorkspace } from "../../invitations/clientNutritionPathResolver";

function buildDuplicateNutritionPlanName(sources, language = "en") {
  return buildNutritionPlanDuplicateDisplayName(sources, language);
}

function normalizeId(value) {
  return String(value ?? "").trim().toLowerCase();
}

function normalizePublishedClientPlanStatus(status) {
  return String(status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
    ? CLIENT_NUTRITION_STATUS_ARCHIVED
    : CLIENT_NUTRITION_STATUS_ACTIVE;
}

async function ensureClientNutritionWriteRouting(clientOrUserId) {
  if (
    clientOrUserId &&
    typeof clientOrUserId === "object" &&
    typeof clientOrUserId.ensureWritableNutritionRouting === "function"
  ) {
    const routingResult = await clientOrUserId.ensureWritableNutritionRouting();
    if (!routingResult?.ok) {
      throw new Error("target client nutrition routing is not writable yet");
    }
  }
}

function resolveClientNutritionWriteWorkspace(clientOrUserId) {
  const workspace = resolveClientNutritionWorkspace(clientOrUserId);
  if (!workspace?.rootDocPathSegments) {
    throw new Error("publishNutritionPlanToClient: missing client or plan");
  }
  return workspace;
}

function setClientNutritionRootDocuments(batch, workspace, updatedAt) {
  batch.set(
    doc(db, ...workspace.rootDocPathSegments),
    {
      schema_version: "nutrition_client_v2",
      storage_scope: "client_editable",
      updatedAt,
    },
    { merge: true },
  );

  if (workspace.scope === "pending" && workspace.workspaceDocPathSegments) {
    batch.set(
      doc(db, ...workspace.workspaceDocPathSegments),
      workspace.buildWorkspaceMetadataPatch({
        lastUpdate: updatedAt,
      }),
      { merge: true },
    );
  }
}

function uniqueNormalizedPlanIds(planIds) {
  return [...new Set((Array.isArray(planIds) ? planIds : []).map(normalizeId).filter(Boolean))];
}

function addRuntimeAssignmentId(planRecord) {
  if (!planRecord || typeof planRecord !== "object") {
    return planRecord;
  }

  return {
    client_plan_id: normalizeId(planRecord?.id),
    ...planRecord,
  };
}

function buildEditableClientPlanRecord({
  planRecord,
  snapshotRootRecord,
  snapshotMeals,
  clientPlanId,
  purpose,
  createdAt,
  updatedAt,
  activatedAt = null,
  deactivatedAt = null,
  originType = "coach_library",
} = {}) {
  const normalizedPlanId = normalizeId(clientPlanId ?? snapshotRootRecord?.id ?? planRecord?.id, "plan");
  const normalizedPurpose = String(
    purpose ?? planRecord?.purpose ?? CLIENT_NUTRITION_DEFAULT_PURPOSE,
  ).trim() || CLIENT_NUTRITION_DEFAULT_PURPOSE;
  const normalizedSnapshotRoot =
    snapshotRootRecord && typeof snapshotRootRecord === "object" ? snapshotRootRecord : {};

  const rootRecord = {
    schema_version: "nutrition_client_plan_v2",
    record_kind: "editable_client_plan",
    id: normalizedPlanId,
    name: String(planRecord?.name ?? normalizedSnapshotRoot?.name ?? "").trim(),
    _rev: 1,
    meal_count:
      Number(
        normalizedSnapshotRoot?.meal_count ??
          (Array.isArray(snapshotMeals) ? snapshotMeals.length : 0),
      ) || 0,
    summary_totals:
      normalizedSnapshotRoot?.summary_totals && typeof normalizedSnapshotRoot.summary_totals === "object"
        ? normalizedSnapshotRoot.summary_totals
        : {
            calories: 0,
            carbs: 0,
            protein: 0,
            fats: 0,
            fiber: 0,
          },
    status: deactivatedAt ? CLIENT_NUTRITION_STATUS_ARCHIVED : CLIENT_NUTRITION_STATUS_ACTIVE,
    purpose: normalizedPurpose,
    origin_type: String(originType || "coach_library").trim() || "coach_library",
    createdAt,
    updatedAt,
  };

  if (activatedAt) {
    rootRecord.activated_at = activatedAt;
  }

  if (deactivatedAt) {
    rootRecord.deactivated_at = deactivatedAt;
  }

  if (
    normalizedSnapshotRoot?.meals_engaged === true ||
    planRecord?.meals_engaged === true ||
    planRecord?.mealsEngaged === true
  ) {
    rootRecord.meals_engaged = true;
  }

  if (normalizedPlanId) {
    rootRecord.id = normalizedPlanId;
  }

  const normalizedOriginPlanId = normalizeId(planRecord?.id ?? normalizedSnapshotRoot?.origin_plan_id);
  if (normalizedOriginPlanId) {
    rootRecord.origin_plan_id = normalizedOriginPlanId;
  }

  const normalizedOriginPlanRev = Number(
    planRecord?._rev ?? normalizedSnapshotRoot?.origin_plan_rev ?? 0,
  );
  if (Number.isFinite(normalizedOriginPlanRev) && normalizedOriginPlanRev > 0) {
    rootRecord.origin_plan_rev = normalizedOriginPlanRev;
  }

  const normalizedNotes = String(planRecord?.notes ?? "").trim();
  if (normalizedNotes) {
    rootRecord.notes = normalizedNotes;
  }

  if (planRecord?.goals && typeof planRecord.goals === "object") {
    rootRecord.goals = planRecord.goals;
  }

  return rootRecord;
}

async function buildEditableClientPlanWrite({
  coach,
  client,
  plan,
  purpose = CLIENT_NUTRITION_DEFAULT_PURPOSE,
  status = CLIENT_NUTRITION_STATUS_ACTIVE,
} = {}) {
  const normalizedPlanId = normalizeId(plan?.id);

  if (!coach?.nutritionStore || !normalizedPlanId) {
    throw new Error("publishNutritionPlanToClient: missing client or plan");
  }

  await ensureClientNutritionWriteRouting(client);
  const nutritionWorkspace = resolveClientNutritionWriteWorkspace(client);

  const sourcePlanEntity =
    plan?.planEntity ??
    coach?.nutritionStore?.planEntitiesById?.get?.(normalizedPlanId) ??
    null;
  const cachedPlanRootRecord =
    coach.nutritionStore.planRootRecordsById.get(normalizedPlanId) ?? null;
  const { planRecord: resolvedPlanRecord, mealRecords } =
    await resolveNutritionPlanActionSnapshot({
      planId: normalizedPlanId,
      sourceEntity: sourcePlanEntity,
      sourceRootRecord: cachedPlanRootRecord,
      fallbackPlan: plan,
      loadMealRecords: (resolvedPlanId, options = {}) =>
        coach.nutritionStore.loadPlanMealRecords(resolvedPlanId, options),
    });

  if (mealRecords.length === 0) {
    throw new Error("publishNutritionPlanToClient: plan copy is incomplete");
  }

  const createdAt = new Date();
  const normalizedStatus = normalizePublishedClientPlanStatus(status);
  const activatedAt =
    normalizedStatus === CLIENT_NUTRITION_STATUS_ARCHIVED ? null : createdAt;
  const deactivatedAt =
    normalizedStatus === CLIENT_NUTRITION_STATUS_ARCHIVED ? createdAt : null;
  const clientPlanId = uuidv4();
  const { rootRecord: snapshotRootRecord, mealRecords: snapshotMeals } = buildClientNutritionPlanSnapshot(
    resolvedPlanRecord,
    {
      clientPlanId,
      originPlanId: normalizedPlanId,
      originPlanRev: Number(cachedPlanRootRecord?._rev ?? plan?._rev ?? 0) || 0,
      purpose,
      createdAt,
      updatedAt: createdAt,
      activatedAt,
      deactivatedAt,
      status: normalizedStatus,
      originType: "coach_library",
    },
  );
  const rootRecord = buildEditableClientPlanRecord({
    planRecord: resolvedPlanRecord,
    snapshotRootRecord,
    snapshotMeals,
    clientPlanId,
    purpose,
    createdAt,
    updatedAt: createdAt,
    activatedAt,
    deactivatedAt,
    originType: "coach_library",
  });

  return {
    nutritionWorkspace,
    updatedAt: createdAt,
    rootRecord,
    snapshotMeals,
  };
}

async function resolveClientPlanCopySource({
  sourcePlan,
  sourceClientPlanStore,
} = {}) {
  const normalizedPlanId = normalizeId(sourcePlan?.id);

  if (!sourceClientPlanStore || !normalizedPlanId) {
    throw new Error("publishNutritionPlanToClient: missing client or plan");
  }

  const sourcePlanEntity =
    sourcePlan?.planEntity ??
    sourceClientPlanStore?.planEntitiesById?.get?.(normalizedPlanId) ??
    null;
  const sourceRootRecord =
    sourcePlan?.rootRecord ??
    sourceClientPlanStore?.planRootRecordsById?.get?.(normalizedPlanId) ??
    null;
  const { planRecord: resolvedPlanRecord, mealRecords: sourceMealRecords } =
    await resolveNutritionPlanActionSnapshot({
      planId: normalizedPlanId,
      sourceEntity: sourcePlanEntity,
      sourceRootRecord,
      fallbackPlan: sourcePlan,
      loadMealRecords: (resolvedPlanId, options = {}) =>
        sourceClientPlanStore.loadPlanMealRecords(resolvedPlanId, options),
    });

  if (sourceMealRecords.length === 0) {
    throw new Error("publishNutritionPlanToClient: plan copy is incomplete");
  }

  return {
    normalizedPlanId,
    resolvedPlanRecord,
    status: normalizePublishedClientPlanStatus(
      sourceRootRecord?.status ?? sourcePlan?.status,
    ),
    purpose:
      String(
        sourceRootRecord?.purpose ??
          sourcePlan?.purpose ??
          CLIENT_NUTRITION_DEFAULT_PURPOSE,
      ).trim() || CLIENT_NUTRITION_DEFAULT_PURPOSE,
  };
}

async function buildClientPlanCopyWrite({
  sourcePlan,
  sourceClientPlanStore,
  targetClient,
  nameOverride,
} = {}) {
  await ensureClientNutritionWriteRouting(targetClient);
  const nutritionWorkspace = resolveClientNutritionWriteWorkspace(targetClient);

  const {
    normalizedPlanId,
    resolvedPlanRecord: rawResolvedPlanRecord,
    status,
    purpose,
  } = await resolveClientPlanCopySource({
    sourcePlan,
    sourceClientPlanStore,
  });
  const resolvedPlanRecord = {
    ...rawResolvedPlanRecord,
    name:
      typeof nameOverride === "string" && nameOverride.trim()
        ? nameOverride.trim()
        : rawResolvedPlanRecord.name,
  };

  if (resolvedPlanRecord.meals.length === 0) {
    throw new Error("publishNutritionPlanToClient: plan copy is incomplete");
  }

  const createdAt = new Date();
  const activatedAt = status === CLIENT_NUTRITION_STATUS_ARCHIVED ? null : createdAt;
  const deactivatedAt = status === CLIENT_NUTRITION_STATUS_ARCHIVED ? createdAt : null;
  const clientPlanId = uuidv4();
  const { rootRecord: snapshotRootRecord, mealRecords: snapshotMeals } =
    buildClientNutritionPlanSnapshot(resolvedPlanRecord, {
      clientPlanId,
      originPlanId: normalizedPlanId,
      originPlanRev: Number(resolvedPlanRecord?._rev ?? sourcePlan?._rev ?? 0) || 0,
      purpose,
      createdAt,
      updatedAt: createdAt,
      activatedAt,
      deactivatedAt,
      status,
      originType: "client_copy",
    });
  const rootRecord = buildEditableClientPlanRecord({
    planRecord: resolvedPlanRecord,
    snapshotRootRecord,
    snapshotMeals,
    clientPlanId,
    purpose,
    createdAt,
    updatedAt: createdAt,
    activatedAt,
    deactivatedAt,
    originType: "client_copy",
  });

  return {
    nutritionWorkspace,
    updatedAt: createdAt,
    rootRecord,
    snapshotMeals,
  };
}

export async function fetchClientNutritionAssignments(clientOrUserId) {
  const nutritionWorkspace = resolveClientNutritionWorkspace(clientOrUserId);
  if (!nutritionWorkspace?.plansCollectionPathSegments) {
    return [];
  }

  const snapshot = await getDocs(
    collection(db, ...nutritionWorkspace.plansCollectionPathSegments),
  );

  return sortClientNutritionPlans(
    snapshot.docs
      .map((planDoc) => ({
        id: planDoc.id,
        ...planDoc.data(),
      }))
      .filter((planRecord) => planRecord?.record_kind === "editable_client_plan")
      .map(addRuntimeAssignmentId),
  );
}

export async function publishNutritionPlanToClient({
  coach,
  client,
  plan,
  purpose = CLIENT_NUTRITION_DEFAULT_PURPOSE,
  status = CLIENT_NUTRITION_STATUS_ACTIVE,
} = {}) {
  const {
    nutritionWorkspace,
    updatedAt,
    rootRecord,
    snapshotMeals,
  } = await buildEditableClientPlanWrite({
    coach,
    client,
    plan,
    purpose,
    status,
  });

  const batch = writeBatch(db);
  setClientNutritionRootDocuments(batch, nutritionWorkspace, updatedAt);
  batch.set(
    doc(db, ...nutritionWorkspace.buildPlanDocPath(rootRecord.id)),
    rootRecord,
  );
  snapshotMeals.forEach((mealRecord) => {
    batch.set(
      doc(db, ...nutritionWorkspace.buildPlanMealDocPath(rootRecord.id, mealRecord.id)),
      mealRecord,
    );
  });
  await batch.commit();

  return addRuntimeAssignmentId(rootRecord);
}

export async function copyClientNutritionPlanToClient({
  sourcePlan,
  sourceClientPlanStore,
  targetClient,
} = {}) {
  const {
    nutritionWorkspace,
    updatedAt,
    rootRecord,
    snapshotMeals,
  } = await buildClientPlanCopyWrite({
    sourcePlan,
    sourceClientPlanStore,
    targetClient,
  });

  const batch = writeBatch(db);
  setClientNutritionRootDocuments(batch, nutritionWorkspace, updatedAt);
  batch.set(
    doc(db, ...nutritionWorkspace.buildPlanDocPath(rootRecord.id)),
    rootRecord,
  );
  snapshotMeals.forEach((mealRecord) => {
    batch.set(
      doc(db, ...nutritionWorkspace.buildPlanMealDocPath(rootRecord.id, mealRecord.id)),
      mealRecord,
    );
  });
  await batch.commit();

  return addRuntimeAssignmentId(rootRecord);
}

export async function duplicateClientNutritionPlanForClient({
  client,
  sourcePlan,
  sourceClientPlanStore,
  language = "en",
} = {}) {
  const normalizedSourcePlanId = normalizeId(sourcePlan?.id);
  const cachedSourceEntity = normalizedSourcePlanId
    ? sourceClientPlanStore?.planEntitiesById?.get?.(normalizedSourcePlanId) ?? null
    : null;
  const cachedSourceRootRecord = normalizedSourcePlanId
    ? sourceClientPlanStore?.planRootRecordsById?.get?.(normalizedSourcePlanId) ?? null
    : null;
  const {
    nutritionWorkspace,
    updatedAt,
    rootRecord,
    snapshotMeals,
  } = await buildClientPlanCopyWrite({
    sourcePlan,
    sourceClientPlanStore,
    targetClient: client,
    nameOverride: buildDuplicateNutritionPlanName([
      sourcePlan?.planEntity?.getDraftSnapshot?.(),
      cachedSourceEntity?.getDraftSnapshot?.(),
      sourcePlan?.planEntity,
      cachedSourceEntity,
      sourcePlan?.rootRecord,
      cachedSourceRootRecord,
      sourcePlan,
    ], language),
  });

  const batch = writeBatch(db);
  setClientNutritionRootDocuments(batch, nutritionWorkspace, updatedAt);
  batch.set(
    doc(db, ...nutritionWorkspace.buildPlanDocPath(rootRecord.id)),
    rootRecord,
  );
  snapshotMeals.forEach((mealRecord) => {
    batch.set(
      doc(db, ...nutritionWorkspace.buildPlanMealDocPath(rootRecord.id, mealRecord.id)),
      mealRecord,
    );
  });
  await batch.commit();

  return addRuntimeAssignmentId(rootRecord);
}

export async function saveClientNutritionAssignmentChanges({
  coach,
  client,
  plan,
  publishPlan = false,
  purpose = CLIENT_NUTRITION_DEFAULT_PURPOSE,
  publishStatus = CLIENT_NUTRITION_STATUS_ACTIVE,
  archiveClientPlanIds = [],
  restoreClientPlanIds = [],
} = {}) {
  await ensureClientNutritionWriteRouting(client);
  const nutritionWorkspace = resolveClientNutritionWriteWorkspace(client);

  const normalizedArchiveIds = uniqueNormalizedPlanIds(archiveClientPlanIds);
  const normalizedRestoreIds = uniqueNormalizedPlanIds(restoreClientPlanIds).filter(
    (planId) => !normalizedArchiveIds.includes(planId),
  );

  const shouldPublish = publishPlan === true && Boolean(plan);
  const hasStatusChanges =
    normalizedArchiveIds.length > 0 || normalizedRestoreIds.length > 0;

  if (!shouldPublish && !hasStatusChanges) {
    return {
      rootRecord: null,
      archivedClientPlanIds: [],
      restoredClientPlanIds: [],
      updatedAt: null,
    };
  }

  const updatedAt = new Date();
  const batch = writeBatch(db);
  let publishedRootRecord = null;

  if (shouldPublish) {
    const { rootRecord, snapshotMeals } = await buildEditableClientPlanWrite({
      coach,
      client,
      plan,
      purpose,
      status: publishStatus,
    });
    publishedRootRecord = rootRecord;

    batch.set(
      doc(db, ...nutritionWorkspace.buildPlanDocPath(rootRecord.id)),
      rootRecord,
    );
    snapshotMeals.forEach((mealRecord) => {
      batch.set(
        doc(db, ...nutritionWorkspace.buildPlanMealDocPath(rootRecord.id, mealRecord.id)),
        mealRecord,
      );
    });
  }

  normalizedArchiveIds.forEach((clientPlanId) => {
    batch.update(
      doc(db, ...nutritionWorkspace.buildPlanDocPath(clientPlanId)),
      {
        status: CLIENT_NUTRITION_STATUS_ARCHIVED,
        deactivated_at: updatedAt,
      },
    );
  });

  normalizedRestoreIds.forEach((clientPlanId) => {
    batch.update(
      doc(db, ...nutritionWorkspace.buildPlanDocPath(clientPlanId)),
      {
        status: CLIENT_NUTRITION_STATUS_ACTIVE,
        activated_at: updatedAt,
        deactivated_at: deleteField(),
      },
    );
  });

  setClientNutritionRootDocuments(batch, nutritionWorkspace, updatedAt);

  await batch.commit();

  return {
    rootRecord: addRuntimeAssignmentId(publishedRootRecord),
    archivedClientPlanIds: normalizedArchiveIds,
    restoredClientPlanIds: normalizedRestoreIds,
    updatedAt,
  };
}

export async function archiveClientNutritionAssignment({
  client,
  clientId,
  clientPlanId,
} = {}) {
  const normalizedClientPlanId = normalizeId(clientPlanId);

  if (!normalizedClientPlanId) {
    throw new Error("archiveClientNutritionAssignment: missing identifiers");
  }

  const clientReference = client ?? clientId;
  await ensureClientNutritionWriteRouting(clientReference);
  const nutritionWorkspace = resolveClientNutritionWriteWorkspace(clientReference);
  const archivedAt = new Date();
  await Promise.all([
    updateDoc(
      doc(db, ...nutritionWorkspace.buildPlanDocPath(normalizedClientPlanId)),
      {
        status: CLIENT_NUTRITION_STATUS_ARCHIVED,
        deactivated_at: archivedAt,
      },
    ),
    setDoc(doc(db, ...nutritionWorkspace.rootDocPathSegments), {
      schema_version: "nutrition_client_v2",
      storage_scope: "client_editable",
      updatedAt: archivedAt,
    }, { merge: true }),
  ]);

  if (nutritionWorkspace.scope === "pending" && nutritionWorkspace.workspaceDocPathSegments) {
    await setDoc(
      doc(db, ...nutritionWorkspace.workspaceDocPathSegments),
      nutritionWorkspace.buildWorkspaceMetadataPatch({
        lastUpdate: archivedAt,
      }),
      { merge: true },
    );
  }

  return {
    id: normalizedClientPlanId,
    client_plan_id: normalizedClientPlanId,
    status: CLIENT_NUTRITION_STATUS_ARCHIVED,
    deactivated_at: archivedAt,
  };
}
